import { isTokenValid, decode } from "./utils/JWT";
import AuthTokenStorage from "./AuthTokenStorage";
import AuthStateStorage from "./AuthStateStorage";

const TOKEN_REVOKE_URL = `${process.env.REACT_APP_API_BASE_URL}/v1/public/auth/revoke`;
const TOKEN_INTROSPECTION_URL = `${process.env.REACT_APP_API_BASE_URL}/v1/public/auth/introspect`;
const GET_TOKENS_URL = `${process.env
  .REACT_APP_API_BASE_URL!}/v1/public/auth/token`;
const IDENTITY_URL = `${process.env
  .REACT_APP_API_BASE_URL!}/v1/private/identity/me`;

type TokenResponse = {
  authorization_token: string;
  refresh_token?: string;
};

const handleTokensResponse = (response: TokenResponse): void => {
  AuthTokenStorage.setAuthorizationToken(response.authorization_token);
  if (response.refresh_token) {
    AuthTokenStorage.setRefreshToken(response.refresh_token);
  }
};

const clearState = () => {
  AuthStateStorage.clear();
};

const clearTokens = () => {
  AuthTokenStorage.clear();
};

const revokeToken = async (
  token: string | null,
  tokenType: string
): Promise<void> => {
  if (!token) {
    return;
  }

  try {
    await fetch(TOKEN_REVOKE_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        token_type: tokenType,
        [tokenType]: token,
      }),
    });
  } catch (err: any) {
    console.error(
      `An error occurred while trying to revoke a ${tokenType}: ${err.message}`
    );
  }
};

const isTokenActive = async (
  token: string | null,
  tokenType: string
): Promise<boolean> => {
  if (!token) {
    return false;
  }

  try {
    const response = await fetch(TOKEN_INTROSPECTION_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        token_type: tokenType,
        [tokenType]: token,
      }),
    });

    const jsonResponse = await response.json();

    if (jsonResponse.success === true && jsonResponse.data?.is_valid === true) {
      return true;
    }
  } catch (err) {
    // Handle errors here
    console.error(err);
  }

  return false;
};

const refreshAuthorizationToken = async (): Promise<boolean> => {
  const refreshToken = AuthTokenStorage.getRefreshToken();

  if (!refreshToken || !(await isTokenActive(refreshToken, "refresh_token"))) {
    return false;
  }

  try {
    const response = await fetch(GET_TOKENS_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        grant_type: "refresh_token",
        refresh_token: refreshToken,
      }),
    });

    const jsonResponse = await response.json();

    if (jsonResponse.success === true && jsonResponse.data) {
      handleTokensResponse(jsonResponse.data);

      return true;
    }
  } catch (err: any) {
    console.error(
      `An error occurred while trying to refresh tokens: ${err.message}`
    );
  }

  return false;
};

const getAuthorizationCodeFromParams = (provided?: string | null): string | null => {
  if (provided) return provided;
  const urlParams = new URLSearchParams(globalThis.location.search);
  return urlParams.get("code");
};

const exchangeAuthorizationCode = async (
  authorizationCode: string
): Promise<TokenResponse | null> => {
  const response = await fetch(GET_TOKENS_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify({
      grant_type: "authorization_code",
      authorization_code: authorizationCode,
      redirect_uri: `${process.env.REACT_APP_USER_POOL_LOGIN_REDIRECT_URL}`,
    }),
  });

  const jsonResponse = await response.json();
  if (jsonResponse.success === true && jsonResponse.data) {
    return jsonResponse.data as TokenResponse;
  }
  return null;
};

const navigateToUrl = (url: string) => {
  if (process.env.NODE_ENV === "development") {
    globalThis.history.pushState(null, "", url);
    globalThis.dispatchEvent(new PopStateEvent("popstate"));
  } else {
    globalThis.location.href = url;
  }
};

const navigateToStateOrBase = (restoreState: boolean) => {
  const state = AuthStateStorage.getAndDestroy();
  if (state && restoreState) {
    navigateToUrl(state.location.href);
    return;
  }

  const baseUrl =
    process.env.NODE_ENV === "development"
      ? globalThis.location.origin
      : `${process.env.REACT_APP_FRONTEND_BASE_URL}`;

  navigateToUrl(baseUrl);
};

const login = async ({
  authorizationCode = null,
  redirect = true,
  restoreState = true,
}: {
  authorizationCode?: string | null;
  redirect?: boolean;
  restoreState?: boolean;
} = {}): Promise<boolean> => {
  const authCode = getAuthorizationCodeFromParams(authorizationCode);
  if (!authCode) return false;

  try {
    const tokenData = await exchangeAuthorizationCode(authCode);
    if (!tokenData) return false;

    handleTokensResponse(tokenData);
    if (redirect) {
      navigateToStateOrBase(restoreState);
    }

    return true;
  } catch (err: any) {
    console.error(
      `An error occurred while obtaining new tokens from auth code: ${err.message}`
    );
  }

  return false;
};

const logout = async ({
  redirect = true,
}: {
  redirect?: boolean;
} = {}): Promise<void> => {
  const refreshToken = AuthTokenStorage.getRefreshToken();

  if (refreshToken) {
    await revokeToken(refreshToken, "refresh_token");
  }

  clearState();
  clearTokens();

  const logoutUrl = refreshToken
    ? `${process.env.REACT_APP_USER_POOL_LOGOUT_URL}`
    : `${process.env.REACT_APP_USER_POOL_LOGIN_URL}`;

  if (redirect && logoutUrl) {
    // Always use window.location.href for logout as it's an external redirect
    globalThis.location.href = logoutUrl;
  }
};

const getValidAuthorizationToken = async ({
  redirectToLogin = true,
}: {
  redirectToLogin?: boolean;
} = {}): Promise<string | null> => {
  const loadValidToken = () => {
    const token = AuthTokenStorage.getAuthorizationToken();
    return token && isTokenValid(token) ? token : null;
  };

  const tryRefreshThenLoad = async () => {
    const refreshOk = await refreshAuthorizationToken();
    if (!refreshOk) return null;
    return loadValidToken();
  };

  const handleError = async (err: any) => {
    console.error(
      `An error occurred while trying to obtain a valid authorization token: ${err.message}`
    );
    const refreshToken = AuthTokenStorage.getRefreshToken();
    if (!refreshToken) return null;
    return tryRefreshThenLoad();
  };

  try {
    return (
      loadValidToken() ??
      (await tryRefreshThenLoad())
    );
  } catch (err: any) {
    const token = await handleError(err);
    if (token) return token;
  }

  if (redirectToLogin) {
    await logout({ redirect: false });
    AuthStateStorage.save();
    globalThis.location.href = `${process.env.REACT_APP_USER_POOL_LOGIN_URL}`;
  }

  return null;
};

const getIdentity = async ({
  redirectToLogin = true,
}: {
  redirectToLogin?: boolean;
} = {}): Promise<any> => {
  try {
    const autorizationToken = await getValidAuthorizationToken({
      redirectToLogin,
    });

    const response = await fetch(IDENTITY_URL, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${autorizationToken}`,
      },
    });

    const jsonResponse = await response.json();

    if (jsonResponse.success === true && jsonResponse.data) {
      return jsonResponse.data;
    }
  } catch (err: any) {
    console.error(
      `An error occurred while obtaining user identity: ${err.message}`
    );
  }

  return null;
};

const getMinimumSessionLifespan = async (): Promise<number> => {
  const refreshToken = AuthTokenStorage.getRefreshToken();

  if (await isTokenActive(refreshToken, "refresh_token")) {
    return 3600;
  }

  const authorizationToken = AuthTokenStorage.getAuthorizationToken();

  if (await isTokenActive(authorizationToken, "authorization_token")) {
    const decodedAuthorizationToken = decode(authorizationToken);

    if (decodedAuthorizationToken?.lifespan) {
      return decodedAuthorizationToken.lifespan;
    }
  }

  return 0;
};

const AuthProvider = {
  logout,
  login,
  getValidAuthorizationToken,
  getIdentity,
  getMinimumSessionLifespan,
};

export default AuthProvider;
